import type { ReactNode } from "react";
import React, { createContext, useContext, useState, useCallback } from "react";

import axios, { type AxiosResponse } from "axios";

import type {
  Organization,
  ProviderDirectoryOrganizationsList200Response,
} from "~/src/types";

export interface OrganizationDataProps {
  results: Organization[];
  loading?: boolean;
  error?: string | null;
  count?: number;
}

interface OrganizationContextType {
  organizationData: OrganizationDataProps;
  fetchOrganizations: (searchParams?: string) => void;
  organizationResults: Organization[];
  setOrganizationData: (data: OrganizationDataProps) => void;
  setOrganizationResults: (results: Organization[]) => void;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(
  undefined,
);

export const OrganizationProvider = ({ children }: { children: ReactNode }) => {
  const [organizationData, setOrganizationData] =
    useState<OrganizationDataProps>({
      results: [],
      loading: true,
      error: null,
    });

  const fetchOrganizations = useCallback((searchParams: string = "") => {
    setOrganizationData((prev) => {
      return {
        ...prev,
        loading: true,
      };
    });

    axios
      .get(
        `provider_directory/organizations${searchParams.startsWith("?") ? "" : "?"}${searchParams}`,
      )
      .then(
        (res: AxiosResponse<ProviderDirectoryOrganizationsList200Response>) => {
          setOrganizationData({
            results: res.data.result?.results || [],
            count: res.data.result?.count || 0,
            loading: false,
            error: null,
          });
        },
      )
      .catch((err) => {
        console.error(err);
        setOrganizationData({
          results: [],
          loading: false,
          error: "Something went wrong loading organizations",
        });
      });
  }, []);

  const setOrganizationDataFunc = useCallback((data: OrganizationDataProps) => {
    setOrganizationData((prev) => {
      return {
        ...prev,
        ...data,
      };
    });
  }, []);

  const setOrganizationResults = useCallback((results: Organization[]) => {
    setOrganizationData((prev) => {
      return {
        ...prev,
        results,
        loading: false,
      };
    });
  }, []);

  const contextValue: OrganizationContextType = {
    organizationData,
    fetchOrganizations,
    organizationResults: organizationData.results,
    setOrganizationData: setOrganizationDataFunc,
    setOrganizationResults,
  };

  return (
    <OrganizationContext.Provider value={contextValue}>
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganizations = () => {
  const context = useContext(OrganizationContext);

  if (context === undefined) {
    throw new Error(
      "useOrganizations must be used within an OrganizationContext",
    );
  }

  return context;
};
